<!-- packages/studio/src/components/army/UnitEditor.vue -->
<template>
  <div class="unit-editor">
    <TitleBar
      :name="modelValue.name"
      noun="Unit"
      :entity-id="modelValue.id"
      id-prefix="unit"
      @rename="$emit('rename', $event)"
      @duplicate="$emit('duplicate')"
      @delete="$emit('delete')"
      @update:entity-id="$emit('update:modelValue', { ...modelValue, id: $event })"
    >
      <template #tabs
        ><StSegmentedControl v-model="tab" :options="tabs" size="sm" on-shell
      /></template>
    </TitleBar>

    <slot name="meta" />

    <div class="unit-editor__pane">
      <UnitStatsForm
        v-if="tab === 'stats'"
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
      />
      <AbilityForm
        v-else-if="tab === 'abilities'"
        :model-value="modelValue.abilities"
        @update:model-value="$emit('update:modelValue', { ...modelValue, abilities: $event })"
      />
      <UnitOptionsForm
        v-else-if="tab === 'wargear'"
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
      />
      <UnitDetailForm
        v-else-if="tab === 'detail'"
        :model-value="modelValue"
        :army-keyword="armyKeyword"
        :alliance="alliance"
        :enhancement-categories="enhancementCategories"
        @update:model-value="$emit('update:modelValue', $event)"
      />
      <BattleProfileForm
        v-else-if="tab === 'composition'"
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
      />
      <WarscrollPreview
        v-else
        :unit="modelValue"
        :army-keyword="armyKeyword"
        :alliance="alliance"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { IUnit, GrandAlliance } from '@scrolldex/shared';
import TitleBar from '../editor/TitleBar.vue';
import { StSegmentedControl, type SegmentOption } from '../ui/index.js';
import AbilityForm from '../AbilityForm.vue';
import UnitStatsForm from './UnitStatsForm.vue';
import UnitDetailForm from './UnitDetailForm.vue';
import UnitOptionsForm from './UnitOptionsForm.vue';
import BattleProfileForm from './BattleProfileForm.vue';
import WarscrollPreview from './WarscrollPreview.vue';

defineProps<{
  modelValue: IUnit;
  armyKeyword?: string;
  alliance?: GrandAlliance;
  // Enhancement category names this unit's army offers (e.g. Heroic Traits,
  // Artefacts of Power, Monstrous Traits). Drives the allowed-enhancement buttons.
  enhancementCategories?: string[];
}>();
defineEmits<{
  (e: 'update:modelValue', value: IUnit): void;
  (e: 'rename', next: string): void;
  (e: 'duplicate'): void;
  (e: 'delete'): void;
}>();
defineSlots<{ meta(): unknown }>();

const tab = ref<string | number>('stats');
const tabs: SegmentOption[] = [
  { value: 'stats', label: 'Stats' },
  { value: 'abilities', label: 'Abilities' },
  { value: 'wargear', label: 'Wargear' },
  { value: 'detail', label: 'Detail' },
  { value: 'composition', label: 'Composition' },
  { value: 'preview', label: 'Preview' },
];
</script>

<style scoped>
.unit-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.unit-editor__pane {
  padding: var(--st-space-3);
  overflow: auto;
}
</style>
